import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input, Select, Field } from '@/components/ui/input'
import { useToast } from '@/providers/UiProviders'
import {
  setWorkflowActions,
  isValidAction,
  AUTO_STR as S,
  type AutoAction,
  type WorkflowRow,
} from './automationApi'

/**
 * A26.02 / A26.03 — workflow action-pipeline editor. Composes the executable
 * action list (`log` / `set_flag`) and saves it via the REAL
 * `PUT /admin/automation/workflows/:id/actions`. Client-side validation mirrors
 * the backend (log needs a message; set_flag key must start with `feature_`).
 *
 * The list endpoint returns only `action_count` (not the actions themselves), so
 * this editor DEFINES/REPLACES the list rather than pre-loading it — the dialog
 * states this explicitly.
 */
export function ActionsEditorDialog({
  workflow,
  open,
  onClose,
  onSaved,
}: {
  workflow: WorkflowRow | null
  open: boolean
  onClose: () => void
  onSaved?: () => void
}) {
  const { toast } = useToast()
  const [actions, setActions] = useState<AutoAction[]>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setActions([])
      setBusy(false)
    }
  }, [open, workflow?.id])

  const allValid = useMemo(
    () => actions.length > 0 && actions.every(isValidAction),
    [actions],
  )

  function patch(i: number, next: Partial<AutoAction>) {
    setActions((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...next } : a)))
  }

  async function save() {
    if (!workflow || !allValid) {
      toast(S.invalidActions, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await setWorkflowActions(workflow.id, actions)
      toast(S.saved, { tone: 'success' })
      onSaved?.()
      onClose()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${S.editActions}${workflow ? ` · ${workflow.name}` : ''}`}
      description={S.redefineNote}
      width="lg"
      footer={
        <>
          <Button variant="primary" disabled={busy || !allValid} onClick={save}>
            {busy ? S.saving : S.save}
          </Button>
          <div className="flex-1" />
          <Button variant="default" onClick={onClose}>
            {S.cancel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {actions.length === 0 && (
          <p className="rounded-md border border-border-subtle bg-surface-1 px-3 py-4 text-center text-sm text-tertiary">
            {S.noActions}
          </p>
        )}

        {actions.map((a, i) => (
          <div
            key={i}
            className="flex flex-wrap items-end gap-2 rounded-md border border-border-default bg-surface-1 p-2.5"
          >
            <Field label={S.actionType} className="w-40">
              <Select
                value={a.type}
                onChange={(e) =>
                  patch(i, { type: e.target.value as AutoAction['type'], message: '', key: '', value: '' })
                }
              >
                <option value="log">{S.actionLog}</option>
                <option value="set_flag">{S.actionSetFlag}</option>
              </Select>
            </Field>

            {a.type === 'log' ? (
              <Field label={S.message} className="min-w-[200px] flex-1">
                <Input
                  value={a.message ?? ''}
                  onChange={(e) => patch(i, { message: e.target.value })}
                  placeholder={S.message}
                />
              </Field>
            ) : (
              <>
                <Field
                  label={S.flagKey}
                  className="min-w-[160px] flex-1"
                  error={
                    (a.key ?? '').length > 0 && !(a.key ?? '').startsWith('feature_')
                      ? S.flagHint
                      : undefined
                  }
                >
                  <Input
                    mono
                    value={a.key ?? ''}
                    onChange={(e) => patch(i, { key: e.target.value })}
                    placeholder="feature_x"
                  />
                </Field>
                <Field label={S.flagValue} className="w-32">
                  <Input
                    value={a.value ?? ''}
                    onChange={(e) => patch(i, { value: e.target.value })}
                    placeholder="true"
                  />
                </Field>
              </>
            )}

            <Button
              variant="ghost"
              size="icon"
              aria-label={S.removeAction}
              onClick={() => setActions((prev) => prev.filter((_, idx) => idx !== i))}
            >
              <Trash2 size={15} />
            </Button>
          </div>
        ))}

        <Button
          variant="default"
          size="sm"
          onClick={() => setActions((prev) => [...prev, { type: 'log', message: '' }])}
        >
          <Plus size={14} />
          {S.addAction}
        </Button>
      </div>
    </Dialog>
  )
}
